import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { useSiteSettings } from "@/lib/SiteSettingsContext";
import {
    isPlaceholderPhone,
    isPlaceholderEmail,
    isPlaceholderAddress,
} from "@/lib/placeholders";

const FALLBACK_OG = "/og-default.png";

function absolute(urlPath) {
    if (!urlPath) return "";
    if (/^https?:\/\//i.test(urlPath)) return urlPath;
    const origin =
        process.env.REACT_APP_SITE_URL ||
        (typeof window !== "undefined" ? window.location.origin : "");
    return `${origin}${urlPath.startsWith("/") ? "" : "/"}${urlPath}`;
}

function isUsableOg(value) {
    if (!value) return false;
    // Allow data URLs (base64) or http(s) URLs or absolute paths
    return value.length < 250000; // sanity cap
}

export default function SEO({
    title,
    description,
    canonical,
    ogType = "website",
    ogImage,
    jsonLd,
    noIndex = false,
}) {
    const location = useLocation();
    const { settings } = useSiteSettings();
    const company = settings.company_name || "Omega IT Ltd";
    const fullTitle = title?.includes(company) ? title : `${title} | ${company}`;
    const origin =
        process.env.REACT_APP_SITE_URL ||
        (typeof window !== "undefined" ? window.location.origin : "");
    const url = canonical || `${origin}${location.pathname}`;
    const img = isUsableOg(ogImage) ? absolute(ogImage) : absolute(FALLBACK_OG);

    const jsonLdBlocks = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];

    return (
        <Helmet>
            <title>{fullTitle}</title>
            {description && <meta name="description" content={description} />}
            <link rel="canonical" href={url} />
            <meta
                name="robots"
                content={noIndex ? "noindex,nofollow" : "index,follow"}
            />
            {/* Open Graph */}
            <meta property="og:site_name" content={company} />
            <meta property="og:type" content={ogType} />
            <meta property="og:title" content={fullTitle} />
            {description && (
                <meta property="og:description" content={description} />
            )}
            <meta property="og:url" content={url} />
            <meta property="og:image" content={img} />
            {/* Twitter */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={fullTitle} />
            {description && (
                <meta name="twitter:description" content={description} />
            )}
            <meta name="twitter:image" content={img} />
            {jsonLdBlocks.map((data, i) => (
                <script key={i} type="application/ld+json">
                    {JSON.stringify(data)}
                </script>
            ))}
        </Helmet>
    );
}

// Helpers to build common JSON-LD structures
const clean = (v) => {
    if (!v) return null;
    const s = String(v).trim();
    if (!s) return null;
    // Reject address/email placeholders using shared logic.
    if (isPlaceholderEmail(s) && /@/.test(s)) return null;
    if (isPlaceholderAddress(s)) return null;
    return s;
};

const cleanEmailForLd = (v) => (isPlaceholderEmail(v) ? null : v.trim());
const cleanAddressForLd = (v) => (isPlaceholderAddress(v) ? null : v.trim());
const cleanPhoneForLd = (v) => (isPlaceholderPhone(v) ? null : v.trim());
const cleanUrlForLd = (v) => {
    if (!v) return null;
    const s = String(v).trim();
    return s || null;
};

export function organizationJsonLd(settings, origin) {
    const sameAs = [settings.instagram_url, settings.facebook_url, settings.linkedin_url]
        .map(cleanUrlForLd)
        .filter(Boolean);
    const data = {
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        name: settings.company_name || "Omega IT Ltd",
        url: origin,
        logo: `${origin}/og-default.png`,
        image: `${origin}/og-default.png`,
        areaServed: "GB",
    };
    const email = cleanEmailForLd(settings.contact_email);
    if (email) data.email = email;
    const tel = cleanPhoneForLd(settings.contact_phone);
    if (tel) data.telephone = tel;
    const addr = cleanAddressForLd(settings.business_address);
    if (addr)
        data.address = {
            "@type": "PostalAddress",
            addressLocality: addr,
            addressCountry: "GB",
        };
    if (sameAs.length) data.sameAs = sameAs;
    return data;
}

export function breadcrumbJsonLd(items) {
    return {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: items.map((it, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: it.name,
            item: it.url,
        })),
    };
}
